import { OptionType } from '@evinced-private/ui-common';

import {
	buildDisplayNameToKeyMap,
	parseLabel
} from 'src/apps/web-sdks/pages/tests/components/shared/helpers';
import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

export interface LabelChangeForUpdate {
	oldLabelValue: string;
	newLabelValue: string;
}

/**
 * Validates that a label change has matching keys (e.g., "environment:prod" -> "environment:production")
 */
export const validateLabelChange = (
	change: LabelChangeForUpdate,
	configuredLabels?: ConfigurationLabel[]
): boolean => {
	const displayNameMap = configuredLabels ? buildDisplayNameToKeyMap(configuredLabels) : undefined;
	const oldParsed = parseLabel(change.oldLabelValue, displayNameMap);
	const newParsed = parseLabel(change.newLabelValue, displayNameMap);

	if (!oldParsed || !newParsed) {
		return false;
	}

	return oldParsed.key.toLowerCase() === newParsed.key.toLowerCase();
};

/**
 * Parses a label change into its key and value components
 */
export const parseLabelChange = (
	change: LabelChangeForUpdate,
	configuredLabels?: ConfigurationLabel[]
): {
	oldParsed: { key: string; value: string };
	newParsed: { key: string; value: string };
} | null => {
	const displayNameMap = configuredLabels ? buildDisplayNameToKeyMap(configuredLabels) : undefined;
	const oldParsed = parseLabel(change.oldLabelValue, displayNameMap);
	const newParsed = parseLabel(change.newLabelValue, displayNameMap);

	if (!oldParsed || !newParsed) {
		return null;
	}

	return { oldParsed, newParsed };
};

/**
 * Finds all row IDs that have a specific label value
 */
export const findRowsWithLabel = (
	testRows: IWebSdkTest[],
	labelValue: string,
	configuredLabels?: ConfigurationLabel[]
): Set<string> => {
	const rowIds = new Set<string>();
	const displayNameMap = configuredLabels ? buildDisplayNameToKeyMap(configuredLabels) : undefined;

	const parsed = parseLabel(labelValue, displayNameMap);
	if (!parsed) {
		return rowIds;
	}

	const normalizedKey = parsed.key.toLowerCase();
	const normalizedValue = parsed.value;

	for (const row of testRows) {
		const rowLabels = row.labels ?? [];
		const hasMatchingLabel = rowLabels.some((rowLabel) => {
			const rowParsed = parseLabel(rowLabel, displayNameMap);
			if (!rowParsed) {
				return false;
			}
			return (
				rowParsed.key.toLowerCase() === normalizedKey &&
				rowParsed.value.toLowerCase() === normalizedValue.toLowerCase()
			);
		});

		if (hasMatchingLabel && row.runId) {
			rowIds.add(row.runId);
		}
	}
	return rowIds;
};

/**
 * Converts a parsed label change into an addition object
 */
export const createAdditionFromChange = (
	oldParsed: { key: string; value: string },
	newParsed: { key: string; value: string }
): { labelType: OptionType; value: OptionType } => {
	return {
		labelType: { value: oldParsed.key, label: oldParsed.key },
		value: { value: newParsed.value, label: newParsed.value }
	};
};

/**
 * Builds a map of configured label keys (lowercase -> actual key)
 */
export const buildLabelKeyMap = (configuredLabels?: ConfigurationLabel[]): Map<string, string> => {
	const labelMap = new Map<string, string>();
	configuredLabels?.forEach((label) => {
		labelMap.set(label.label.toLowerCase(), label.label);
	});
	return labelMap;
};

/**
 * Gets the configured label key (or falls back to the provided key)
 */
export const getConfiguredLabelKey = (key: string, labelMap: Map<string, string>): string => {
	return labelMap.get(key.toLowerCase()) || key;
};
